import { checkClaimsWeb, checkTier } from "@eventr/common";
import { ColoursObj } from "@eventr/common-storybook";
import { Box } from "@mui/material";
import { useGuestStore } from "feat/guest/guest.store";
import { useScreenLayout } from "feat/layout/screen-layout";
import { useHasVerticalBar } from "feat/nativeNavigation/vertical-bar";
import { FC, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "routing";
import { useBoundStore } from "state/bound.store";
import { useTopInset } from "state/windowState/window.store";
import { useShallow } from "zustand/react/shallow";
import ActivityFeedButton from "./slide/newComps/ActivityFeedButton";
import CarouselZoneToggle from "./slide/newComps/CarouselZoneToggle";
import HitBoxIcon from "./slide/newComps/HitBoxIcon";
import CarouselSeeAllItineraries from "./slide/newComps/seeAllItineraries";
import { VerticalBarHeaderControls } from "./VerticalBarHeaderControls";

const VISUAL_SPACING = 66;

/** Whether the expenses button shows: the tier includes expenses, the org has not turned them off, and the user may add them. */
export function useShowExpensesButton(): boolean {
  const { expenseTrackingMode, tier } = useBoundStore(
    useShallow((s) => ({ expenseTrackingMode: s.orgConfig.expenseTrackingMode, tier: s.tier })),
  );
  const hasExpenseTrackingPermission = tier ? checkTier(tier, "ExpenseTracking") : false;
  return checkClaimsWeb([`/`], ["addExpenses"]) && expenseTrackingMode !== "disabled" && hasExpenseTrackingPermission;
}

export const CarouselOverlay: FC = () => {
  const navigate = useNavigate();
  const showExpenses = useShowExpensesButton();

  const topInset = useTopInset();

  const Wrapper = useCallback(
    (children: React.ReactNode, top = topInset + 8, right?: number) => {
      return (
        <Box
          sx={{
            position: "absolute",
            zIndex: 1000,
            top: top,
            // In the column, when the header runs on under a vertical bar (see CarouselContainer).
            ...(right === undefined
              ? { left: "var(--header-bleed-left, 0px)" }
              : { right: `calc(${right}px + var(--header-bleed-right, 0px))` }),
          }}
        >
          {children}
        </Box>
      );
    },
    [topInset],
  );

  const isGuest = useGuestStore.getState().isGuestMode;
  // The tab rail, or the vertical bar on iOS, carries the menu, expenses, alerts and time mode.
  const verticalBar = useHasVerticalBar();
  const { pathname } = useLocation();
  const onRail = useScreenLayout().tabBar === "rail" || verticalBar;

  return (
    <>
      {/* Home's controls, published by Home's own header rather than the copies heading Chat and Team. */}
      {verticalBar && pathname === routes.home.path && <VerticalBarHeaderControls />}
      {!isGuest && !onRail && Wrapper(<CarouselSeeAllItineraries />)}

      {!onRail && Wrapper(<CarouselZoneToggle />, undefined, 0)}

      {showExpenses &&
        !onRail &&
        Wrapper(
          <HitBoxIcon
            id="carousel-expenses"
            icon="Billing"
            colour={ColoursObj.shades.shade5}
            onClick={() => navigate("/edit/expenses")}
            align="end"
          />,
          topInset + VISUAL_SPACING,
        )}

      {!isGuest && !onRail && Wrapper(<ActivityFeedButton />, topInset + VISUAL_SPACING, 0)}
    </>
  );
};
